import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Products', href: '/products' },
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'About', href: '/about' },
  { label: 'Where to Buy', href: '/where-to-buy' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBg = isHome
    ? scrolled
      ? 'bg-white/95 backdrop-blur-md border-b border-stone-100'
      : 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md border-b border-stone-100';

  const textColor = isHome && !scrolled ? 'text-white' : 'text-stone-800';
  const logoColor = isHome && !scrolled ? 'text-white' : 'text-[#0B5C4D]';

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-[#FF6B35] rounded-lg flex-shrink-0">
              <i className="ri-sun-fill text-white text-xl" />
            </div>
            <div>
              <span className={`font-black text-lg tracking-tight leading-none block ${logoColor} transition-colors duration-300`}>
                PHIX<span className="text-[#FF6B35]"> DOCTOR</span>
              </span>
              <span className={`text-[9px] font-semibold uppercase tracking-widest leading-none ${isHome && !scrolled ? 'text-white/60' : 'text-stone-400'}`}>
                Sun-Powered Repair
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-semibold tracking-wide uppercase hover:text-[#FF6B35] transition-colors duration-200 cursor-pointer ${textColor}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Header CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/products"
              className="whitespace-nowrap bg-[#FF6B35] text-white text-sm font-bold px-5 py-2.5 rounded-full hover:bg-[#e55a25] transition-colors duration-200 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-shopping-bag-line text-sm" />
              Shop Now
            </Link>
          </div>

          {/* Mobile burger */}
          <button
            className="md:hidden cursor-pointer p-1"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${textColor}`} />
            </div>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-stone-800 font-semibold text-sm uppercase tracking-wide cursor-pointer hover:text-[#FF6B35] transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/products"
              className="whitespace-nowrap bg-[#FF6B35] text-white text-sm font-bold px-5 py-3 rounded-full text-center hover:bg-[#e55a25] transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              Shop Repair Kits
            </Link>
          </div>
        )}
      </header>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-stone-200 px-4 py-3 flex gap-3">
        <Link
          to="/products"
          className="whitespace-nowrap flex-1 bg-[#FF6B35] text-white text-sm font-bold py-3 rounded-full text-center hover:bg-[#e55a25] transition-colors cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-shopping-bag-line" />
          Shop Kits Now
        </Link>
        <a
          href="/#how-it-works"
          className="whitespace-nowrap flex-1 border-2 border-[#0B5C4D] text-[#0B5C4D] text-sm font-bold py-3 rounded-full text-center cursor-pointer flex items-center justify-center gap-2"
        >
          <i className="ri-sun-line" />
          How It Works
        </a>
      </div>
    </>
  );
}
